import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TransactionHistory = ({ transactions, onDeleteTransaction }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const filterOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'crypto', label: 'Crypto Only' },
    { value: 'stock', label: 'Stocks Only' },
    { value: 'realized', label: 'Realized Only' },
    { value: 'unrealized', label: 'Unrealized Only' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Date' },
    { value: 'asset', label: 'Asset' },
    { value: 'type', label: 'Type' },
    { value: 'value', label: 'Value' },
    { value: 'gainLoss', label: 'Gain/Loss' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFilteredTransactions = () => {
    let filtered = transactions;

    // Apply filters
    switch (filter) {
      case 'crypto':
        filtered = filtered?.filter(t => t?.assetType === 'crypto');
        break;
      case 'stock':
        filtered = filtered?.filter(t => t?.assetType === 'stock');
        break;
      case 'realized':
        filtered = filtered?.filter(t => t?.realized);
        break;
      case 'unrealized':
        filtered = filtered?.filter(t => !t?.realized);
        break;
      default:
        break;
    }

    // Apply sorting
    const sorted = [...filtered]?.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'date':
          aVal = new Date(a?.date);
          bVal = new Date(b?.date);
          break;
        case 'asset':
          aVal = a?.asset;
          bVal = b?.asset;
          break;
        case 'type':
          aVal = a?.type;
          bVal = b?.type;
          break;
        case 'value':
          aVal = a?.totalValue || 0;
          bVal = b?.totalValue || 0;
          break;
        case 'gainLoss':
          aVal = a?.realizedGainLoss || a?.unrealizedGainLoss || 0;
          bVal = b?.realizedGainLoss || b?.unrealizedGainLoss || 0;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      buy: 'text-success bg-success/10',
      sell: 'text-destructive bg-destructive/10',
      trade: 'text-warning bg-warning/10',
      mining: 'text-primary bg-primary/10',
      staking: 'text-primary bg-primary/10',
      airdrop: 'text-success bg-success/10',
      fork: 'text-warning bg-warning/10'
    };
    return colors?.[type] || 'text-muted-foreground bg-muted/10';
  };

  const getGainLossDisplay = (transaction) => {
    const gainLoss = transaction?.realized 
      ? transaction?.realizedGainLoss 
      : transaction?.unrealizedGainLoss;
    
    if (!gainLoss && gainLoss !== 0) return '-';
    
    const color = gainLoss >= 0 ? 'text-success' : 'text-destructive';
    const prefix = gainLoss >= 0 ? '+' : '';
    
    return (
      <span className={color}>
        {prefix}{formatCurrency(gainLoss)}
      </span>
    );
  };

  const filteredTransactions = getFilteredTransactions();

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-card-foreground">Transaction History</h2>
          <span className="text-sm text-muted-foreground">
            {filteredTransactions?.length} transaction{filteredTransactions?.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Filters and Sorting */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Select
            label="Filter"
            options={filterOptions}
            value={filter}
            onChange={setFilter}
          />
          
          <Select
            label="Sort By"
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
          />
          
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Sort Order
            </label>
            <div className="flex border border-border rounded-md">
              <button
                onClick={() => setSortOrder('desc')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-l-md transition-standard ${
                  sortOrder === 'desc' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                Desc
              </button>
              <button
                onClick={() => setSortOrder('asc')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-r-md transition-standard ${
                  sortOrder === 'asc' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                Asc
              </button>
            </div>
          </div>
        </div>

        {/* Transaction Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-2 font-medium text-muted-foreground">Date</th>
                <th className="text-left py-3 px-2 font-medium text-muted-foreground">Type</th>
                <th className="text-left py-3 px-2 font-medium text-muted-foreground">Asset</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Quantity</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Price</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Total Value</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Gain/Loss</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Status</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions?.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-8">
                    <div className="flex flex-col items-center space-y-2">
                      <Icon name="Database" size={48} className="text-muted-foreground/50" />
                      <p className="text-muted-foreground">No transactions found</p>
                      <p className="text-xs text-muted-foreground">
                        Add transactions to see your tax calculations
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTransactions?.map((transaction) => (
                  <tr key={transaction?.id} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-2 text-card-foreground">
                      {formatDate(transaction?.date)}
                    </td>
                    <td className="py-3 px-2">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        getTransactionTypeColor(transaction?.type)
                      }`}>
                        {transaction?.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-card-foreground">{transaction?.asset}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          transaction?.assetType === 'crypto' ?'bg-orange-100 text-orange-700' :'bg-blue-100 text-blue-700'
                        }`}>
                          {transaction?.assetType}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right text-card-foreground">
                      {transaction?.quantity?.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-right text-card-foreground">
                      {formatCurrency(transaction?.price)}
                    </td>
                    <td className="py-3 px-2 text-right text-card-foreground">
                      {formatCurrency(transaction?.totalValue)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {getGainLossDisplay(transaction)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        transaction?.realized 
                          ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                      }`}>
                        {transaction?.realized ? 'Realized' : 'Unrealized'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteTransaction(transaction?.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;